/**
 * The sass layer's doors into the toast store.
 *
 * A sassy toast used to be text and nothing else — the same lime rectangle the
 * app used to confirm a write, with a joke in it instead of a fact. It is now
 * a different object: her colour, her mark, a size up, and its own sound where
 * the moment earns one. That distinction is the point. A person should be able
 * to tell, without reading a word, whether the app is confirming something or
 * whether she is having an opinion about it.
 *
 * Durations are gone from this file. They belong to the sentence now, not to
 * the category — see `durationFor`.
 */

import {
  BURST_WINDOW_MS,
  notableReasons,
  type SassContext,
  type SassEvent,
  shouldSassLog,
  trophyByKey,
} from '@gramas/shared'
import { api, hasSession } from '../api/client.js'
import { ceremonyForAward, offerForAwards } from '../ceremony/detect.js'
import { type ChipDef, chipsFor, followUpChip } from '../glitime/chips.js'
import { affectionNow, clearCooled, takeChipSlot } from '../glitime/interaction.js'
import { flickerMark } from '../glitime/markSignal.js'
import { maybePromiseToast } from '../glitime/promises.js'
import { currentPresence } from '../presence/store.js'
import { showSassToast, showToast, showTrophyToast, type ToastAction } from '../shell/toastStore.js'
import { register, sassLine } from './engine.js'

/**
 * The drop-in for an existing toast call: the sassy line when the voice is on,
 * the exact neutral string when it is not. Every call site keeps its neutral
 * message in place — that string IS the off state, not a fallback of last resort.
 *
 * The neutral string is deliberately still a plain utility toast. In the dry
 * register she is muzzled, not absent, but a lime confirmation is what the app
 * saying "done" looks like, and dressing one up in her colour would be putting
 * her face on a sentence she did not write.
 */
export function sassToast(
  event: SassEvent,
  ctx: Partial<SassContext>,
  neutral: string,
  action?: ToastAction,
): void {
  const line = sassLine(event, ctx)
  if (line != null) showSassToast(line, action)
  else showToast(neutral, action)
}

/**
 * How many log toasts have already been spoken in the current burst, and when
 * the last one landed.
 *
 * Counted in *spoken* toasts rather than in log commits on purpose: "Ir dar
 * vienas" only makes sense as an answer to something she actually said. If the
 * first two entries of a dinner passed in silence, the third is not a
 * continuation of a conversation that never started.
 */
let lastSpokeAt = 0
let burstCount = 0

/** Test-only: forget the current burst, so one test's dinner is not another's. */
export function __resetBurstForTests(): void {
  lastSpokeAt = 0
  burstCount = 0
}

/** How often a spoken log toast offers reply chips, before the daily cap. */
const CHIP_OFFER_RATE = 1 / 4

/** How often a food the taxonomy does not know earns a model-written quip. */
const QUIP_RATE = 1 / 3

/**
 * The exotic-food quip: she read what he actually wrote, and it deserved a
 * comment the catalogue could never have prepared. Async by construction —
 * the ordinary (or silent) reaction has already happened synchronously, and
 * this follows a few seconds later only if the model produced something that
 * passed the runtime voice lint server-side. Every failure is silence: a
 * treat that apologises for not arriving is not a treat. Rationed here by
 * the coin and server-side by a small daily budget.
 */
function maybeQuip(ctx: SassContext): void {
  if (register() !== 'sassy') return
  if (ctx.foodClass != null) return
  const name = ctx.itemName?.trim()
  if (name == null || name.length < 3) return
  if (typeof navigator === 'undefined' || !navigator.onLine) return
  if (!hasSession()) return
  // Its own coin, not the caller's rng: the tests drive that one through the
  // main decision and an extra draw here would shift every seeded sequence.
  if (Math.random() >= QUIP_RATE) return
  void api
    .post<{ quip: string }>('/api/ai/quip', { name: name.slice(0, 120) })
    .then((result) => {
      if (result.quip) showSassToast(result.quip)
    })
    .catch(() => {
      // Silent by design; the entry was already reacted to.
    })
}

/** How often a comeback stays in the conversation with one more chip. */
const FOLLOW_UP_RATE = 1 / 3

/**
 * Her answer to a tapped chip, in the mood and the context the original line
 * was spoken in.
 *
 * The context matters more here than anywhere: „Labai blogai?" has to answer
 * with the actual overage, and „O tu kaip?" has to answer with the mood the
 * presence store is currently publishing — a comeback that could have been
 * written before he tapped is not an answer, it is a fortune cookie.
 *
 * `depth` caps the exchange at two rounds. The second comeback never offers
 * anything further: two is a moment, three is a chat, and the chat has its
 * own door in her panel.
 */
function comeback(chip: ChipDef, ctx: SassContext, depth: number, rng: () => number): () => void {
  return () => {
    if (chip.id === 'atsiprasau') clearCooled()
    const line = sassLine('chip_comeback', {
      ...ctx,
      surface: chip.id,
      mood: currentPresence().mood,
    })
    if (line == null) return
    const next = depth === 0 && rng() < FOLLOW_UP_RATE ? followUpChip(chip.id, rng) : null
    showSassToast(
      line,
      undefined,
      undefined,
      next == null ? undefined : [{ label: next.label, run: comeback(next, ctx, depth + 1, rng) }],
    )
  }
}

/**
 * The ways to talk back at this particular line, or undefined for a toast
 * that stays one-directional (most of them — a chip on every line would turn
 * her into a questionnaire).
 *
 * Which chips appear is the whole point: `chipsFor` only offers what somebody
 * would actually have said at this moment, so „Labai blogai?" exists on a day
 * that went over and nowhere else. Sassy register only, capped per day in the
 * interaction store — except while she is cooled, when the single apology
 * chip is offered off the ration, because a repair should never be rationed.
 */
function replyChips(ctx: SassContext, rng: () => number): ToastAction[] | undefined {
  if (register() !== 'sassy') return undefined
  const cooled = affectionNow() === 'cooled'
  if (!cooled) {
    if (rng() >= CHIP_OFFER_RATE) return undefined
    if (!takeChipSlot()) return undefined
  }
  const chips = chipsFor(ctx, cooled, rng)
  if (chips.length === 0) return undefined
  return chips.map((chip) => ({ label: chip.label, run: comeback(chip, ctx, 0, rng) }))
}

/**
 * The gated log-success path. Most logs stay silent — the ring filling is
 * still the confirmation — but notable ones always speak and two thirds of the
 * rest do. Returns whether a visible toast fired, so the caller can skip its
 * screen-reader announce() and keep the live region to one item per action.
 */
export function maybeLogToast(
  ctx: Partial<SassContext>,
  now: number = Date.now(),
  rng: () => number = Math.random,
): boolean {
  const burst = now - lastSpokeAt < BURST_WINDOW_MS ? burstCount : 0
  const withHour: SassContext = { hour: new Date().getHours(), burst, ...ctx }
  // A promise being broken outranks everything, the silent third included:
  // her memory is not subject to her whims. The promise toast replaces the
  // ordinary line — the same entry roasted twice is a scold, not a character.
  if (maybePromiseToast(withHour.itemName)) {
    lastSpokeAt = now
    burstCount = burst + 1
    return true
  }
  maybeQuip(withHour)
  const line = shouldSassLog(withHour, rng) ? sassLine('log_success', withHour) : null
  if (line == null) {
    // She noticed and let it pass. Without this the silent third of logs was
    // indistinguishable from the app having missed the entry entirely — the
    // ring moves, but the ring moves for everything. A flicker is the smallest
    // possible "yes, I saw that".
    flickerMark()
    return false
  }
  // A sound only where the log was worth remarking on. Every meal chiming
  // would be a kitchen timer, and she is not one.
  showSassToast(
    line,
    undefined,
    notableReasons(withHour).length > 0 ? 'notable' : undefined,
    replyChips(withHour, rng),
  )
  lastSpokeAt = now
  burstCount = burst + 1
  return true
}

/**
 * Announce awards that fired just now, one after another.
 *
 * This used to schedule its own `setTimeout` chain, on the stated assumption
 * that the store showed one toast at a time — which it did not; it showed
 * three and silently dropped the oldest. Two trophies landing on one commit (a
 * hundredth entry that is also a seventh straight day) could therefore delete
 * each other. The store queues now, so this hands over all of them at once and
 * they arrive in order, each holding the screen for as long as its own line
 * needs.
 *
 * The dry register gets the title alone: a muzzled voice states the fact and
 * stops.
 */
export function announceAwards(keys: readonly string[]): void {
  const dry = register() === 'dry'
  for (const key of keys) {
    const def = trophyByKey(key)
    if (def == null) continue
    // Three of the eighteen get a stage instead. Announcing them here as well
    // would be the same news delivered twice, once in a corner of the screen
    // the ceremony is already covering.
    if (ceremonyForAward(key) != null) continue
    showTrophyToast(dry ? `${def.title}. ${def.description}` : def.line, 'trophy')
  }
  void offerForAwards(keys)
}
